import type { IGroupAllocations } from '@experiences/interfaces';

import type { IGroup } from '../../common/interfaces/cis/group';
import { updateUserGroups } from '../../store/action/UserProfileAction';
import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';
import { getIdentityBaseRoute } from './IdentityServiceUtils';

// Kept as SWR cache key (not used as actual request URL anymore)
export const groupUrl = `/api/identity/Group`;

const getCisGroupUrl = (partitionGlobalId: string, isHostMode: boolean) =>
    `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/Group`;

export interface IGroupPayload {
    partitionGlobalId: string;
    name: string;
    id?: string;
}

export interface ICreateGroupPayload extends IGroupPayload {
    id: string;
    directoryUserMemberIDs: string[];
}

export interface IEditGroupPayload extends IGroupPayload {
    directoryUserIDsToAdd: string[];
    directoryUserIDsToRemove: string[];
}

export function getGroups({
    partitionGlobalId, isHostMode,
}: { url: string; partitionGlobalId: string; isHostMode: boolean }) {
    return get<IGroup[]>(`${getCisGroupUrl(partitionGlobalId, isHostMode)}/${partitionGlobalId}`, { ignoreBasePath: true });
}

export function getGroupById({
    partitionGlobalId, id, isHostMode,
}: { url?: string; partitionGlobalId: string; id: string; isHostMode: boolean }) {
    return get<IGroup>(`${getCisGroupUrl(partitionGlobalId, isHostMode)}/${partitionGlobalId}/${id}`, { ignoreBasePath: true });
}

export async function getGroupsWithLicenses({
    partitionGlobalId, isHostMode,
}: { url?: string; partitionGlobalId: string; isHostMode: boolean }) {
    const [ groups, groupLicenses ] = await Promise.all([
        get<IGroup[]>(`${getCisGroupUrl(partitionGlobalId, isHostMode)}/${partitionGlobalId}`, { ignoreBasePath: true }),
        get<IGroupAllocations[]>(`/lease_/api/account/${partitionGlobalId}/user-license/groups`, { stripPortalPath: true }),
    ]);
    return groups.map(group => ({
        ...group,
        ...groupLicenses.find(gl => gl.id === group.id),
    }));
}

export async function createGroup(payload: ICreateGroupPayload, isCurrentUser: boolean, isHostMode: boolean) {
    const group = await post<IGroup>(
        `${getCisGroupUrl(payload.partitionGlobalId, isHostMode)}`,
        {
            body: payload,
            ignoreBasePath: true,
        },
    );

    if (group && isCurrentUser) {
        await updateUserGroups('create', [ group.id ]);
    }

    return group;
}

export async function editGroup(
    id: string,
    payload: IEditGroupPayload,
    user: { type: 'create' | 'delete'; isCurrentUser: boolean },
    isHostMode: boolean,
) {
    const group = await put<IGroup>(
        `${getCisGroupUrl(payload.partitionGlobalId, isHostMode)}/${id}`,
        {
            body: payload,
            ignoreBasePath: true,
        },
    );

    // Side-effect: notify License Accountant about removed users (fire-and-forget, matching server behavior)
    if (payload.directoryUserIDsToRemove?.length) {
        axiosDelete(`/lease_/api/account/${payload.partitionGlobalId}/user-license/group/${id}/user`, {
            body: { userIds: payload.directoryUserIDsToRemove },
            stripPortalPath: true,
        }).catch(() => { /* server also swallows this error */ });
    }

    if (group && user.isCurrentUser) {
        await updateUserGroups(user.type, [ group.id ]);
    }

    return group;
}

export async function deleteGroups(partitionGlobalId: string, payload: { groupIDs: string[] }, isCurrentUser: boolean, isHostMode: boolean) {
    await axiosDelete(
        `${getCisGroupUrl(partitionGlobalId, isHostMode)}/${partitionGlobalId}`,
        {
            body: payload,
            ignoreBasePath: true,
        },
    );

    // Side-effect: remove license rules for deleted groups (fire-and-forget, matching server behavior)
    await Promise.all(
        payload.groupIDs.map(groupId =>
            axiosDelete(`/lease_/api/account/${partitionGlobalId}/user-license/group/${groupId}`, { stripPortalPath: true })
                .catch(() => { /* server also swallows this error */ })
        )
    );

    if (isCurrentUser) {
        await updateUserGroups('delete', payload.groupIDs);
    }
}

export async function checkIfOnlyUserInGroup({
    partitionGlobalId, userGlobalId, groupId, isHostMode,
}: { url?: string; partitionGlobalId: string; userGlobalId: string; groupId?: string; isHostMode: boolean }) {
    const group = await get<IGroup>(
        `${getCisGroupUrl(partitionGlobalId, isHostMode)}/${partitionGlobalId}/${groupId}`,
        { ignoreBasePath: true },
    );
    const found = group.members?.some(user => user.identifier.toLowerCase() === userGlobalId.toLowerCase());
    if (!found) {
        return false;
    }
    return found && group.members.filter(user => user.objectType !== 'DirectoryRobotUser').length === 1;
}
